import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BrandMark } from './Brand';
import { APP_URLS, NETWORK_MENUS, profileLinks, webURL } from '../config/header';

export interface HeaderAction {
  label: string;
  onSelect: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  separatorBefore?: boolean;
}
interface Props {
  leading: React.ReactNode;
  controls: React.ReactNode;
  fileActions: HeaderAction[];
  onHome: () => void;
}
type OpenMenu = { id: string; trigger: HTMLButtonElement; focus: 'first' | 'last' | null };

/** PicPlayer's two-row chrome, with React-owned dropdowns and lifecycle cleanup. */
export const AppHeader: React.FC<Props> = ({ leading, controls, fileActions, onHome }) => {
  const [open, setOpen] = useState<OpenMenu | null>(null);
  const [networkTab, setNetworkTab] = useState(0);
  const [position, setPosition] = useState({ top: 51, left: 8, maxHeight: 400 });
  const menuRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const typed = useRef({ text: '', at: 0 });
  const links = profileLinks();
  const network = NETWORK_MENUS.find((menu) => `network-${menu.label.toLowerCase()}` === open?.id);

  const close = useCallback((restore = false) => {
    if (restore) open?.trigger.focus({ preventScroll: true });
    setOpen(null);
    typed.current = { text: '', at: 0 };
  }, [open]);
  const choices = () => Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') || []);

  useLayoutEffect(() => {
    if (!open || !menuRef.current) return;
    const place = () => {
      const rect = open.trigger.getBoundingClientRect();
      const width = menuRef.current?.getBoundingClientRect().width || 245;
      const top = rect.bottom + 3;
      setPosition({ top, left: Math.max(8, Math.min(open.id === 'profile' ? rect.right - width : rect.left, window.innerWidth - width - 8)), maxHeight: Math.max(40, window.innerHeight - top - 8) });
    };
    place();
    if (open.focus) {
      const items = choices();
      (open.focus === 'last' ? items.at(-1) : items[0])?.focus({ preventScroll: true });
    }
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const outside = (event: Event) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target) && !headerRef.current?.contains(target)) close();
    };
    const focus = (event: FocusEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target) && target !== open.trigger) close();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', focus);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('focusin', focus); };
  }, [open, close]);

  const openFrom = (id: string, trigger: HTMLButtonElement, focus: OpenMenu['focus'] = null) => setOpen({ id, trigger, focus });
  const moveAcross = (trigger: HTMLButtonElement, direction: number, expand: boolean) => {
    const siblings = Array.from(trigger.closest('[role="menubar"]')?.querySelectorAll<HTMLButtonElement>('button[data-header-menu]') || []);
    if (!siblings.length) return;
    const next = siblings[(siblings.indexOf(trigger) + direction + siblings.length) % siblings.length];
    if (next.dataset.networkIndex) setNetworkTab(Number(next.dataset.networkIndex));
    next.focus({ preventScroll: true });
    next.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (expand) openFrom(next.dataset.headerMenu!, next, 'first');
  };
  const triggerKey = (event: React.KeyboardEvent<HTMLButtonElement>, id: string) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault(); event.stopPropagation(); openFrom(id, event.currentTarget, event.key === 'ArrowUp' ? 'last' : 'first');
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault(); event.stopPropagation(); moveAcross(event.currentTarget, event.key === 'ArrowRight' ? 1 : -1, !!open);
    } else if (event.key === 'Escape') {
      event.preventDefault(); event.stopPropagation(); close(true);
    } else if (event.key === 'Tab') close();
  };
  const triggerProps = (id: string) => ({
    id: `ss-trigger-${id}`,
    'data-header-menu': id,
    'aria-haspopup': 'menu' as const,
    'aria-expanded': open?.id === id,
    'aria-controls': open?.id === id ? 'ss-header-popover' : undefined,
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
      if (open?.id === id) close(); else openFrom(id, event.currentTarget, event.detail === 0 ? 'first' : null);
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => triggerKey(event, id),
    onPointerEnter: (event: React.PointerEvent<HTMLButtonElement>) => {
      if (event.pointerType === 'mouse' && open && open.id !== 'profile' && id !== 'profile' && open.id !== id) openFrom(id, event.currentTarget);
    },
  });
  const menuKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Never let menu navigation move selected objects on the canvas.
    event.stopPropagation();
    if (event.key === 'Escape') { event.preventDefault(); close(true); return; }
    if (event.key === 'Tab') { close(true); return; }
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (['ArrowLeft', 'ArrowRight'].includes(event.key) && open) {
      event.preventDefault(); moveAcross(open.trigger, event.key === 'ArrowRight' ? 1 : -1, true); return;
    }
    const items = choices();
    const current = items.indexOf(document.activeElement as HTMLElement);
    let index: number | undefined;
    if (event.key === 'ArrowDown') index = (current + 1) % items.length;
    if (event.key === 'ArrowUp') index = (current - 1 + items.length) % items.length;
    if (event.key === 'Home') index = 0;
    if (event.key === 'End') index = items.length - 1;
    if (index !== undefined) { event.preventDefault(); items[index]?.focus(); return; }
    if (event.key.length === 1 && event.key !== ' ') {
      event.preventDefault();
      const now = Date.now();
      typed.current = { text: (now - typed.current.at < 600 ? typed.current.text : '') + event.key.toLowerCase(), at: now };
      const sorted = [...items.slice(current + 1), ...items.slice(0, current + 1)];
      sorted.find(item => item.textContent?.trim().toLowerCase().startsWith(typed.current.text))?.focus();
    }
  };
  const accountLink = (value: string) => {
    const href = webURL(value);
    if (!href) return null;
    const url = new URL(href);
    url.searchParams.set('returnTo', window.location.href);
    return url.href;
  };
  const login = accountLink(links.login), join = accountLink(links.join);

  return <>
    <header className="ss-header" ref={headerRef} aria-label="StoryboardSmith application header">
      <div className="ss-titlebar">
        <button type="button" className="ss-brand-button" onClick={onHome} aria-label="StoryboardSmith home"><BrandMark tagline="Comic Creator" /></button>
        <nav className="ss-menu-bar ss-file-menu" role="menubar" aria-label="Application menu">
          <button type="button" role="menuitem" {...triggerProps('file')}>File</button>
        </nav>
        <nav className="ss-menu-bar ss-network-menu" role="menubar" aria-label="Network menu">
          {NETWORK_MENUS.map((menu, index) => <button type="button" role="menuitem" key={menu.label} data-network-index={index} tabIndex={networkTab === index ? 0 : -1} onFocus={() => setNetworkTab(index)} {...triggerProps(`network-${menu.label.toLowerCase()}`)}>{menu.label}</button>)}
        </nav>
      </div>
      <div className="ss-optionsbar">
        <div className="ss-header-leading">{leading}</div>
        <div className="ss-toolbar-scroll" aria-label="Comic controls"><div className="ss-toolbar">{controls}</div></div>
        <div className="ss-profile-wrap">
          <button type="button" className="ss-profile-button" aria-label="User profile — log in or join" title="User profile" {...triggerProps('profile')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="8" r="3.5" /><path d="M5 20v-1a7 7 0 0 1 14 0v1" /></svg>
          </button>
        </div>
      </div>
    </header>
    {open && createPortal(<div ref={menuRef} id="ss-header-popover" className={`ss-menu-popover ${network ? 'ss-network-popover' : ''}`} role="menu" aria-labelledby={`ss-trigger-${open.id}`} style={position} onKeyDown={menuKey}>
      {open.id === 'file' && fileActions.map((item, index) => <React.Fragment key={item.label}>
        {item.separatorBefore && index > 0 && <div className="ss-menu-separator" role="separator" />}
        <button type="button" role="menuitem" tabIndex={-1} disabled={item.disabled} onClick={() => { close(true); item.onSelect(); }}>{item.icon}<span>{item.label}</span></button>
      </React.Fragment>)}
      {network?.groups.map((group, groupIndex) => <React.Fragment key={groupIndex}>
        {groupIndex > 0 && <div className="ss-menu-separator" role="separator" />}
        {group.map(name => name === 'StoryboardSmith' ?
          <button type="button" role="menuitem" tabIndex={-1} aria-current="page" key={name} onClick={() => close(true)}><span>{name}</span><small>Current app</small></button> :
          <a key={name} href={webURL(APP_URLS[name]) || undefined} role="menuitem" tabIndex={-1} target="_blank" rel="noopener noreferrer" onClick={() => close(true)}>{name}</a>)}
      </React.Fragment>)}
      {open.id === 'profile' && <>
        <div className="ss-profile-summary" role="presentation"><strong>Your account</strong><span>{login || join ? 'Connect your account' : 'Local profile · comics stay on this device'}</span></div>
        <div className="ss-menu-separator" role="separator" />
        {login ? <a role="menuitem" tabIndex={-1} href={login} target="_blank" rel="noopener noreferrer" onClick={() => close(true)}>Log in</a> : <button type="button" role="menuitem" tabIndex={-1} disabled title="A StoryboardSmith authentication service has not been configured.">Log in</button>}
        {join ? <a role="menuitem" tabIndex={-1} href={join} target="_blank" rel="noopener noreferrer" onClick={() => close(true)}>Join</a> : <button type="button" role="menuitem" tabIndex={-1} disabled title="A StoryboardSmith authentication service has not been configured.">Join</button>}
        {!login && !join && <p className="ss-account-note">Account sign-in is not configured. You can create and save comics locally without an account.</p>}
        <div className="ss-menu-separator" role="separator" />
        <a role="menuitem" tabIndex={-1} href={links.terms} target="_blank" rel="noopener noreferrer" onClick={() => close(true)}>Terms of service</a>
        <a role="menuitem" tabIndex={-1} href={links.privacy} target="_blank" rel="noopener noreferrer" onClick={() => close(true)}>Privacy policy</a>
        <button type="button" role="menuitem" tabIndex={-1} onClick={() => { close(true); document.dispatchEvent(new CustomEvent('picplayer:ad-preferences', { cancelable: true })); }}>Ad choices</button>
      </>}
    </div>, document.body)}
  </>;
};
